import { Api, Currency, DateUtil, LogManager, ModelRow } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import {
    BlurEvent, ChangeEvent, Layout, LayoutProvidedReportDecoratorProps, PanelProcessLastRun, PanelProcessLastRunVariant,
    ReportPreview, ReportViewerTab, SlideoutDecorator, Tab, TableSelectionEvent
} from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { AutogenLayoutReverseSettlementCheck } from "./autogen/AutogenLayoutReverseSettlementCheck";

const log = LogManager.getLogger("lme.settlement.ReverseSettlementCheck");

export class ReverseSettlementCheck extends AutogenLayoutReverseSettlementCheck {
    private calculationPromise: Promise<void>;
    private _reportPreview: ReportPreview;
    private processLog: PanelProcessLastRun;

    onLoad(): void | Promise<void> {
        this.tableChecks.addSelectionListener((event: TableSelectionEvent) => {
            if (this.calculationPromise) {
                this.calculationPromise = this.calculationPromise.then(async () => this.handleTableSelection());
            } else {
                this.calculationPromise = this.handleTableSelection();
            }
        });

        this.processLog = PanelProcessLastRun.create("Reverse Settlement", {
            variant: PanelProcessLastRunVariant.compact,
            marginTop: "20"
        });
        if (this.processLog) {
            this.panelProcessLog.add(this.processLog);
        }
        this.setupComponentListeners();
        this.populateFromUrlAndSearch();
    }

    private populateFromUrlAndSearch(): void {
        try {
            const params = new URLSearchParams(window.location.search);
            if (params.size === 0) return;

            const checkNumberFrom = params.get("checkNumberFrom");
            const checkNumberTo = params.get("checkNumberTo");
            const checkDateFrom = params.get("checkDateFrom");
            const checkDateTo = params.get("checkDateTo");
            const payeeIdSearch = params.get("payeeIdSearch");
            const factoringCompanyIdSearch = params.get("factoringCompanyIdSearch");
            const voidDate = params.get("voidDate");

            // Populate search row IDs so lookups bind correctly
            if (payeeIdSearch) this.mainDataSource.searchRow.set("payee_id", payeeIdSearch);
            if (factoringCompanyIdSearch) this.mainDataSource.searchRow.set("factoring_company_id", factoringCompanyIdSearch);

            // Display search row to controls
            this.panelSearchFields.displayData(this.mainDataSource.searchRow, null, 0);

            // Populate text/range fields
            if (checkNumberFrom) this.textboxCheckNumberFrom.text = checkNumberFrom;
            if (checkNumberTo) this.textboxCheckNumberTo.text = checkNumberTo;
            if (checkDateFrom) this.textboxCheckDateFrom.text = checkDateFrom;
            if (checkDateTo) this.textboxCheckDateTo.text = checkDateTo ?? checkDateFrom;

            // Void Date defaults to Check Date if not provided
            if (voidDate) {
                this.textboxVoidDate.text = voidDate;
            } else if (checkDateFrom) {
                this.textboxVoidDate.text = checkDateFrom;
            }

            // Trigger search
            // Use the same logic as clicking the Search button
            const filter = this.mainDataSource.getSearchFilter();
            this.populateFilterWithRangeData(filter);
            if (this.mainDataSource.validate(true)) {
                this.mainDataSource.search(filter.data, null, null);
            }
        } catch (error) {
            log.error("Error populating Reverse Settlement Check from URL params:", error);
        }
    }

    private async handleTableSelection() {
        const selectedRows = this.tableChecks.selectedRows;
        const totalAmount = new Currency(0);
        for (const row of selectedRows) {
            const modelRow = row.data as ModelRow;
            await totalAmount.add(modelRow.getCurrency("amount"));
        }
        this.buttonReverseChecks.caption = `Reverse (${selectedRows.length}) ${totalAmount}`;
        this.buttonReverseChecks.enabled = selectedRows.length > 0;
        this.validateVoidDate();
    }

    setupComponentListeners(): void {
        this.textboxCheckDateFrom.addBlurListener((event: BlurEvent) => {
            if (this.textboxCheckDateTo.isEmpty()) {
                this.textboxCheckDateTo.text = this.textboxCheckDateFrom.text;
            }
        });
        this.textboxCheckNumberFrom.addBlurListener((event: BlurEvent) => {
            if (!this.textboxCheckNumberFrom.isEmpty()) {
                this.textboxCheckNumberFrom.text = this.textboxCheckNumberFrom.text.padStart(8, '0');
            }
            if (this.textboxCheckNumberTo.isEmpty()) {
                this.textboxCheckNumberTo.text = this.textboxCheckNumberFrom.text;
            }
        });
        this.textboxCheckNumberTo.addBlurListener((event: BlurEvent) => {
            if (!this.textboxCheckNumberTo.isEmpty()) {
                this.textboxCheckNumberTo.text = this.textboxCheckNumberTo.text.padStart(8, '0');
            }
        });
        this.buttonReverseChecks.addClickListener(async () => {
            if (this.tableChecks.selectedRows.length > 0) {
                const useCheckDate: boolean = await this.processVoidDate();
                if (useCheckDate === undefined) {
                    return;
                }
                const selectedRows = this.tableChecks.selectedRows;
                const checkRecordIds = selectedRows.map(row => row.data.get("id")).join(",");
                this.buttonReverseChecks.busy = true;
                Api.post("lme/settlement/reverse-settlement-check", {
                    check_record_ids: checkRecordIds,
                    void_date: this.textboxVoidDate.getDataValue(),
                    check_number_from: this.textboxCheckNumberFrom.getDataValue(),
                    check_number_to: this.textboxCheckNumberTo.getDataValue(),
                    check_date_from: this.textboxCheckDateFrom.getDataValue(),
                    cash_account: this.mainDataSource.searchRow.get("bank_account_id"),
                    payee_id: this.mainDataSource.searchRow.get("payee_id"),
                    factoring_company_id: this.mainDataSource.searchRow.get("factoring_company_id"),
                    use_check_date: useCheckDate
                }).then(response => {
                    const data = response.data[0];
                    const documents = data["documents"];
                    const successfulCheckIds: [] = data["successful_check_ids"];
                    this.reportPreview.data = documents;
                    for (const id of successfulCheckIds) {
                        const row = selectedRows.find(r => r.data.get("id") === id);
                        this.tableChecks.removeRow(row.data);
                    }
                    const layout = new Layout({ needsServerLayout: false, components: [ this.reportPreview ] });
                    new SlideoutDecorator({
                        layout: layout,
                        width: 1200,
                        fillVerticalSpace: true,
                        title: "Reports"
                    });
                }).catch(error => {
                    log.error("Error reversing settlement checks", error);
                    CommonDialogs.showError(error);
                }).finally(() => {
                    this.buttonReverseChecks.busy = false;
                    this.processLog?.refreshLastProcessedData();
                });
            }
        });
        this.textboxVoidDate.addChangeListener((event: ChangeEvent) => {
            this.validateVoidDate();
        });
    }

    private get reportPreview(): ReportPreview {
        this.initializeReportPreview();
        return this._reportPreview;
    }

    private set reportPreview(value: ReportPreview) {
        this._reportPreview = value;
    }

    private initializeReportPreview() {
        if (this._reportPreview == null) {
            const previewProps: Partial<LayoutProvidedReportDecoratorProps> = {
                reportPreviewTitle: "",
                tabCreationCallback: (tab: ReportViewerTab) => {
                    tab.imageName = ImageName.document;
                    tab.headingImage.rowBreak = false;
                    tab.headingImage.tooltip = tab.reportData.reportName;
                }
            };
            this.reportPreview = new ReportPreview(previewProps);
        }
    }

    populateFilterWithRangeData(filter: ModelRow): void {
        filter.set("checkDateFrom", this.textboxCheckDateFrom.text);
        filter.set("checkDateTo", this.textboxCheckDateTo.text);
        filter.set("checkNumberFrom", this.textboxCheckNumberFrom.text);
        filter.set("checkNumberTo", this.textboxCheckNumberTo.text);
    }

    buttonClearOnClick(): void {
        this.textboxCheckNumberFrom.clear();
        this.textboxCheckNumberTo.clear();
        this.textboxCheckDateFrom.clear();
        this.textboxCheckDateTo.clear();
        this.mainDataSource.searchRow.clear();
        this.panelSearchFields.displayData(this.mainDataSource.searchRow, null, 0);
        this.tableChecks.clearRows();
    }

    styleTab(tab: Tab) {
        tab.heading.setProps({ color: "subtle.light", fontBold: false, borderWidth: 0 });
        tab.headingLabel.setProps({ color: "McLeodPrimary", fontSize: "xlarge", fontBold: true });
    }

    validateVoidDate(): void {
        if (!this.textboxVoidDate.validateSimple()) return;
        const allCheckDatesBeforeVoidDate = this.tableChecks.selectedRows.every(row => {
            const modelRow = row.data as ModelRow;
            return modelRow.getDate("check_date") <= DateUtil.parseDateWithKeywords(this.textboxVoidDate.getDataValue());
        });
        if (!allCheckDatesBeforeVoidDate && !this.textboxVoidDate.isEmpty()) {
            this.textboxVoidDate.color = "warning";
            this.textboxVoidDate.tooltip = "Void Date is before some of the Check Dates selected";
        } else {
            this.textboxVoidDate.color = null;
            this.textboxVoidDate.tooltip = null;
        }
    }

    async processVoidDate(): Promise<boolean> {
        if (this.textboxVoidDate.color === "warning") {
            return await CommonDialogs.showYesNo("Void date must be equal to or greater than the check "
                + "date. Would you like to use the check date on the check(s) with "
                + "a date greater than the void date entered?", "Warning").then(response => {
                return response;
            });
        }
        return new Promise(resolve => resolve(true));
    }
}
